import { Link } from 'react-router-dom';
import { useProjects } from '../../api/projects';
import { ProjectStatus } from '@spi/shared';
import { ProjectCard } from './projects/ProjectCard';
import { PageMeta } from './shared/PageMeta';

export function MarketplaceLanding() {
  const { data: activeProjects } = useProjects({ status: [ProjectStatus.ACTIVE], limit: 3 });

  return (
    <div>
      <PageMeta
        title="Second Chance Seed Network"
        description="Connect seed donors with communities in need. Give seeds, grow communities, and receive IRS tax deductions."
        path="/marketplace"
      />

      {/* Hero */}
      <section className="relative overflow-hidden py-20 md:py-32">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <img src="/images/marketplace-hero-bg.jpg" alt="" className="w-full h-full object-cover" loading="eager" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-spi-cream/80 to-white" />
        </div>

        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 blob-green rounded-full" />
          <div className="absolute bottom-20 right-10 w-96 h-96 blob-orange rounded-full" />
        </div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-spi-seed border border-spi-parchment mb-6">
              <span className="seed-dot" />
              <span className="text-sm font-medium text-spi-earth">Second Chance Seed Network</span>
            </div>
            <h1 className="mb-6">
              Give Seeds,{' '}
              <span className="text-gradient-green">Grow Communities</span>
            </h1>
            <p className="text-xl text-spi-slate max-w-2xl mx-auto mb-10">
              Connect vetted seed donors with verified non-profits and schools worldwide.
              Every donation receives IRS Form 8283 for tax deductions.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/marketplace/apply?role=donor" className="btn btn-primary text-lg px-8">
                Apply to Donate
              </Link>
              <Link to="/marketplace/apply?role=recipient" className="btn btn-secondary text-lg px-8">
                Apply as Recipient
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Three simple steps to make a difference</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: '1',
                title: 'Apply & Get Vetted',
                desc: 'Submit an application as a donor or recipient. Our team reviews your organization and creates your account.',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                ),
              },
              {
                step: '2',
                title: 'Browse & Connect',
                desc: 'Recipients create projects with seed requests. Donors browse and pledge to fulfill them.',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                ),
              },
              {
                step: '3',
                title: 'Ship, Verify & Deduct',
                desc: 'Ship seeds, get admin verification, and receive IRS Form 8283 for your tax deduction.',
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                ),
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-spi-green to-spi-green-dark flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {item.icon}
                  </svg>
                </div>
                <h3 className="text-xl mb-2">{item.title}</h3>
                <p className="text-sm text-spi-slate">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-pattern-seeds">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="card p-8">
              <h3 className="text-2xl mb-4 text-spi-green-dark">For Donors</h3>
              <ul className="space-y-3">
                {[
                  'IRS Form 8283 generated automatically for tax deductions',
                  'All recipients vetted and approved by admin team',
                  'Full transparency from pledge to delivery verification',
                  'Track your impact with donation history and stats',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-spi-charcoal">
                    <svg className="w-5 h-5 text-spi-green flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card p-8">
              <h3 className="text-2xl mb-4 text-spi-orange-dark">For Recipients</h3>
              <ul className="space-y-3">
                {[
                  'Create projects with detailed seed requests',
                  'Connect with verified institutional donors',
                  'Admin support throughout the entire process',
                  'Track incoming donations and delivery status',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-spi-charcoal">
                    <svg className="w-5 h-5 text-spi-orange flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      {activeProjects?.data.length ? (
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="section-header">
              <h2>Featured Projects</h2>
              <p>Communities in need of seeds right now</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {activeProjects.data.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/marketplace/projects" className="btn btn-outline">
                View All Projects
              </Link>
            </div>
          </div>
        </section>
      ) : null}

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto card card-dark p-12 text-center">
            <h2 className="text-3xl mb-4">Ready to Make a Difference?</h2>
            <p className="text-white/80 mb-8 text-lg">
              Join the Second Chance Seed Network and help communities grow.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/marketplace/apply" className="btn bg-white text-spi-green-dark hover:bg-spi-cream text-lg px-8">
                Apply for an Account
              </Link>
              <Link to="/marketplace/projects" className="btn border-2 border-white/30 text-white hover:bg-white/10 text-lg px-8">
                Browse Projects
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
